const REQUEST_TIMEOUT_MS = 30000
const STORAGE_IDLE_TIMEOUT_MS = 60000
// Fetch exposes download progress, but not upload progress. Allow even a
// single oversized geometry row to upload at 1 KiB/s without a fixed cap.
const MIN_UPLOAD_BYTES_PER_SECOND = 1024
// The Response constructor rejects any body, even an empty one, for these.
// WebView fetch still exposes an empty stream for them (PostgREST's 204s).
const NULL_BODY_STATUSES = new Set([204, 205, 304])

function requestBodyBytes(
  input: RequestInfo | URL,
  init?: RequestInit,
): number {
  const body = init?.body
  if (typeof body === "string") return new TextEncoder().encode(body).byteLength
  if (body instanceof Blob) return body.size
  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body))
    return body.byteLength
  if (body instanceof URLSearchParams)
    return new TextEncoder().encode(body.toString()).byteLength
  const headers = new Headers(
    init?.headers ?? (input instanceof Request ? input.headers : undefined),
  )
  const contentLength = Number(headers.get("content-length"))
  return Number.isFinite(contentLength) && contentLength > 0 ? contentLength : 0
}

export const networkTimeoutError = () =>
  new TypeError("Failed to fetch: network request timed out")

/**
 * Mobile Supabase requests must release serial upload/auth locks on a silent
 * link. Keep the deadline through the body, including error responses: auth-js
 * must see a rejected fetch (AuthRetryableFetchError), never a synthetic 4xx or
 * a body parsing error that could discard the session.
 *
 * All response deadlines reset on received bytes; Storage gets a longer idle
 * deadline. REST's pre-response budget also scales with known upload size.
 * Auth keeps its short pre-response deadline. These
 * APIs consume complete JSON/blob bodies, so buffer before returning to keep
 * body stalls on the same retryable fetch-error path as connection stalls.
 */
export function createFetchWithTimeout(
  fetcher: typeof fetch,
  { requestIdleTimeoutMs = REQUEST_TIMEOUT_MS } = {},
): typeof fetch {
  return async (input, init) => {
    const url = input instanceof Request ? input.url : String(input)
    const pathname = new URL(url, import.meta.env.VITE_SUPABASE_URL).pathname
    const timeoutMs = pathname.startsWith("/storage/v1/")
      ? STORAGE_IDLE_TIMEOUT_MS
      : requestIdleTimeoutMs
    const uploadAllowanceMs = pathname.startsWith("/rest/v1/")
      ? Math.ceil(
          (requestBodyBytes(input, init) / MIN_UPLOAD_BYTES_PER_SECOND) * 1000,
        )
      : 0
    const deadline = new AbortController()
    const callerSignal =
      init?.signal ?? (input instanceof Request ? input.signal : undefined)
    const signals = callerSignal
      ? [callerSignal, deadline.signal]
      : [deadline.signal]
    const fallback = new AbortController()
    const forwardAbort = () => {
      const aborted = signals.find((candidate) => candidate.aborted)
      if (aborted) fallback.abort(aborted.reason)
    }
    const signal =
      typeof AbortSignal.any === "function"
        ? AbortSignal.any(signals)
        : fallback.signal
    if (signal === fallback.signal) {
      for (const source of signals)
        source.addEventListener("abort", forwardAbort, { once: true })
      forwardAbort()
    }

    let timer: ReturnType<typeof setTimeout> | undefined
    const resetDeadline = (delayMs = timeoutMs) => {
      clearTimeout(timer)
      timer = setTimeout(() => deadline.abort(networkTimeoutError()), delayMs)
    }
    let rejectOnAbort!: () => void
    const aborted = new Promise<never>((_resolve, reject) => {
      rejectOnAbort = () => reject(signal.reason)
      signal.addEventListener("abort", rejectOnAbort, { once: true })
      if (signal.aborted) rejectOnAbort()
    })
    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined
    resetDeadline(timeoutMs + uploadAllowanceMs)
    try {
      if (signal.aborted) return await aborted
      const response = await Promise.race([
        fetcher(input, { ...init, signal }),
        aborted,
      ])
      if (!response.body) return response
      resetDeadline()
      reader = response.body.getReader()
      const chunks: Uint8Array[] = []
      while (true) {
        const { done, value } = await Promise.race([reader.read(), aborted])
        if (done) break
        chunks.push(value)
        if (value.byteLength > 0) resetDeadline()
      }
      const body = NULL_BODY_STATUSES.has(response.status)
        ? null
        : new Blob(chunks)
      const buffered = new Response(body, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      })
      Object.defineProperties(buffered, {
        url: { value: response.url },
        redirected: { value: response.redirected },
        type: { value: response.type },
      })
      return buffered
    } catch (error) {
      // Do not wait for a broken native stream's cancellation to release us.
      void reader?.cancel(error).catch(() => {})
      throw error
    } finally {
      clearTimeout(timer)
      signal.removeEventListener("abort", rejectOnAbort)
      for (const source of signals)
        source.removeEventListener("abort", forwardAbort)
      try {
        reader?.releaseLock()
      } catch {
        // Cleanup on older WebViews must not replace the network result/error.
      }
    }
  }
}

export const fetchWithTimeout: typeof fetch = (input, init) =>
  createFetchWithTimeout(fetch)(input, init)
